import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from '../styles/inventoryModal.module.css';
import { apiService } from '../services/api';
import { InventoryService } from '../services/inventoryService';
import { EncryptionService } from '../services/encryptionService';
import { useDialog } from '../context/DialogContext';
import { useLocale } from '../context/LocaleContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';

interface InventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  onInventoryUpdate?: (inventoryType: string) => void;
  currentVersions?: Record<string, string>;
  inventoryStatuses?: any[];
  initialTab?: string;
}

// Stored values stay stable; labels resolve through t() at render time so locale switches apply.
const INVENTORY_TYPES = [
  {
    key: 'resentments',
    labelKey: 'step.4.inventory.type.resentments',
    myLabelKey: 'step.4.inventory.my.resentments',
  },
  { key: 'fears', labelKey: 'step.4.inventory.type.fears', myLabelKey: 'step.4.inventory.my.fears' },
  { key: 'harms', labelKey: 'step.4.inventory.type.harms', myLabelKey: 'step.4.inventory.my.harms' },
];

const IMPACT_OPTIONS = [
  { value: 'self-esteem', labelKey: 'step.4.inventory.impact.self_esteem' },
  { value: 'pride', labelKey: 'step.4.inventory.impact.pride' },
  { value: 'pocketbook', labelKey: 'step.4.inventory.impact.pocketbook' },
  { value: 'personal-relationships', labelKey: 'step.4.inventory.impact.personal_relationships' },
  { value: 'sexual-relationships', labelKey: 'step.4.inventory.impact.sexual_relationships' },
  { value: 'emotional-security', labelKey: 'step.4.inventory.impact.emotional_security' },
  { value: 'ambitions', labelKey: 'step.4.inventory.impact.ambitions' },
];

const DEFECT_OPTIONS = [
  { value: 'controlling', labelKey: 'step.4.inventory.defect.controlling' },
  { value: 'dishonest', labelKey: 'step.4.inventory.defect.dishonest' },
  { value: 'disengaged', labelKey: 'step.4.inventory.defect.disengaged' },
  { value: 'entitled', labelKey: 'step.4.inventory.defect.entitled' },
  { value: 'frightened', labelKey: 'step.4.inventory.defect.frightened' },
  { value: 'greedy', labelKey: 'step.4.inventory.defect.greedy' },
  { value: 'inconsiderate', labelKey: 'step.4.inventory.defect.inconsiderate' },
  { value: 'impulsive', labelKey: 'step.4.inventory.defect.impulsive' },
  { value: 'lustful', labelKey: 'step.4.inventory.defect.lustful' },
  { value: 'self-pity', labelKey: 'step.4.inventory.defect.self_pity' },
  { value: 'selfish', labelKey: 'step.4.inventory.defect.selfish' },
  { value: 'willful', labelKey: 'step.4.inventory.defect.willful' },
];

type InventoryData = Record<string, any[]>;

function getDefaultItem(type: string): any {
  switch (type) {
    case 'resentments':
      return { who: '', why: '', feeling: '', impacts: [], myPart: '' };
    case 'fears':
      return { fear: '', why: '', impacts: [] };
    case 'harms':
      return { whoHarmed: '', how: '', defects: [], alternative: '' };
    default:
      return {};
  }
}

function isComplete(type: string, item: any): boolean {
  if (!item) return false;
  
  if (type === 'resentments') {
    return item.who && item.why && item.feeling && Array.isArray(item.impacts) && item.impacts.length > 0 && item.myPart;
  }
  if (type === 'fears') {
    return item.fear && item.why && Array.isArray(item.impacts) && item.impacts.length > 0;
  }
  if (type === 'harms') {
    return item.whoHarmed && item.how && Array.isArray(item.defects) && item.defects.length > 0 && item.alternative;
  }
  return false;
}



const InventoryModal: React.FC<InventoryModalProps> = ({ isOpen, onClose, user, onInventoryUpdate, currentVersions, inventoryStatuses, initialTab }) => {
  const { showConfirm: showConfirmDialog, showAlert, showHelp: showHelpDialog } = useDialog();
  const { t } = useLocale();
  const [currentType, setCurrentType] = useState(initialTab || 'resentments');
  const [data, setData] = useState<InventoryData>({});
  const [entryInput, setEntryInput] = useState('');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form, setForm] = useState<any>(null);

  // Variant is tracked separately so styling never depends on the (translated) message text.
  const [importStatus, setImportStatus] = useState<{ text: string; error: boolean } | null>(null);
  const [completionStatus, setCompletionStatus] = useState<Record<string, boolean>>({});
  const completionStatusLoadedRef = useRef(false);
  
  // Auto-save state management
  const [savingEntry, setSavingEntry] = useState<number | null>(null);
  const [savedEntry, setSavedEntry] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { handleError } = useAuthErrorHandler();
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const periodicSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update currentType when initialTab changes (e.g., when modal opens with different tab)
  useEffect(() => {
    if (isOpen && initialTab) {
      setCurrentType(initialTab);
    }
  }, [isOpen, initialTab]);


  // Helper function to get the current version name for each inventory type
  const getCurrentVersionName = useCallback(() => {
    if (currentVersions && currentVersions[currentType]) {
      return currentVersions[currentType];
    }
    // Fallback to the old naming convention if no currentVersions provided
    return `${currentType}-current`;
  }, [currentType, currentVersions]);

  // Localized display label for an inventory type, resolved when needed so locale switches apply
  const typeLabel = (typeKey: string) => {
    const config = INVENTORY_TYPES.find(type => type.key === typeKey);
    return config ? t(config.labelKey) : typeKey;
  };

  const currentTypeLabel = () => typeLabel(currentType);

  type InventoryTabVisualState = 'empty' | 'progress' | 'complete';

  const getInventoryTabVisualState = (typeKey: string): InventoryTabVisualState => {
    if (completionStatus[typeKey]) return 'complete';
    const status = inventoryStatuses?.find((s) => s.type === typeKey);
    const total = status?.total || 0;
    if (total === 0) return 'empty';
    return 'progress';
  };

  const mobileTabStateIcon = (state: InventoryTabVisualState): string => {
    if (state === 'complete') return '✓';
    if (state === 'progress') return '●';
    return '○';
  };

  const completedInventoryCount = INVENTORY_TYPES.filter((type) => completionStatus[type.key]).length;

  // Use inventory statuses from props instead of loading from API
  // Only load data on initial modal open, not when inventoryStatuses changes
  // (to prevent race conditions where adding items triggers reloads that overwrite optimistic updates)
  const dataLoadedRef = useRef(false);
  
  // Reset dataLoaded flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      dataLoadedRef.current = false;
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (!inventoryStatuses || !isOpen) return;
    
    // Only load data on initial open, not on every inventoryStatuses change
    if (dataLoadedRef.current) return;
    dataLoadedRef.current = true;
    
    // Convert inventory statuses to the data format expected by the modal
    const allData: InventoryData = {};
    
    for (const status of inventoryStatuses) {
      // For now, we'll still need to load the actual items from API
      // but we can use the status to determine if there are items
      if (status.total > 0) {
        // Load the actual items for this type
        const loadItems = async () => {
          try {
            const versionName = currentVersions && currentVersions[status.type] 
              ? currentVersions[status.type] 
              : `${status.type}-current`;
            const items = await InventoryService.getInventory(status.type, versionName);
            const filteredItems = items.filter(item => item._type !== 'completion_status');
            allData[status.type] = filteredItems;
            setData(prev => ({ ...prev, [status.type]: filteredItems }));
          } catch (error) {
            console.error(`Error loading ${status.type} data:`, error);
            allData[status.type] = [];
            setData(prev => ({ ...prev, [status.type]: [] }));
          }
        };
        loadItems();
      } else {
        allData[status.type] = [];
        setData(prev => ({ ...prev, [status.type]: [] }));
      }
    }
  }, [isOpen, currentVersions]); // Removed inventoryStatuses from dependencies to prevent reloads

  // Load completion status from API
  useEffect(() => {
    const loadCompletionStatus = async () => {
      if (!user || Object.keys(data).length === 0) return;
      

      
      try {
        const status: Record<string, boolean> = {};
        
        for (const type of INVENTORY_TYPES) {
          try {
            const versionName = currentVersions && currentVersions[type.key] 
              ? currentVersions[type.key] 
              : `${type.key}-current`;
            const completionData = await InventoryService.getInventoryCompletion(type.key, versionName);
            status[type.key] = completionData.completed || false;
    
          } catch (error) {
            console.error(`Error loading completion status for ${type.key}:`, error);
            status[type.key] = false;
          }
        }
        

        setCompletionStatus(status);
        completionStatusLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading completion status:', error);
      }
    };

    loadCompletionStatus();
  }, [user, data, currentVersions]);

  const getCurrentData = () => {
    const currentData = data[currentType] || [];
    // Filter out completion status items when getting current data
    return currentData.filter(item => item._type !== 'completion_status');
  };

  // Immediate save function (bypasses debounce) - used for critical operations
  const immediateSaveEntry = useCallback(async (idx: number, formData: any): Promise<boolean> => {
    const versionName = getCurrentVersionName();
    const currentData = data[currentType] || [];
    
    // Check if data actually changed
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(currentData[idx]);
    if (!hasChanges) return true;

    try {
      const newData = [...currentData];
      newData[idx] = formData;

      await InventoryService.saveInventory(currentType, versionName, newData);

      // Update local state
      setData(prev => ({
        ...prev,
        [currentType]: newData
      }));

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);

      // Notify parent to update counters
      if (onInventoryUpdate) {
        onInventoryUpdate(currentType);
      }

      return true;
    } catch (error: any) {
      console.error('Error saving entry:', error);
      
      // Check if it's an authentication error using the hook
      const isAuthError = error?.name === 'AuthenticationError' || 
                         error?.constructor?.name === 'AuthenticationError';
      
      if (isAuthError) {
        showAlert({
          title: t('step.4.inventory.dialog.session_expired_title'),
          message: t('step.4.inventory.dialog.session_expired_changes'),
          okText: t('step.4.inventory.dialog.ok')
        });
      }
      
      return false;
    }
  }, [currentType, data, getCurrentVersionName, onInventoryUpdate, t]);

  // Auto-save function with debouncing
  const autoSaveEntry = useCallback(async (idx: number, formData: any) => {
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set debounce timeout (300ms)
    saveTimeoutRef.current = setTimeout(async () => {
      const versionName = getCurrentVersionName();
      const currentData = data[currentType] || [];
      
      // Check if data actually changed (compare to raw data, not filtered)
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(currentData[idx]);
      if (!hasChanges) {
        setHasUnsavedChanges(false);
        return;
      }

      // Set saving state
      setSavingEntry(idx);
      setSaveError(null);
      setSavedEntry(null);

      try {
        const newData = [...currentData];
        newData[idx] = formData;

        await InventoryService.saveInventory(currentType, versionName, newData);

        // Update local state
        setData(prev => ({
          ...prev,
          [currentType]: newData
        }));

        // Show success feedback
        setSavingEntry(null);
        setSavedEntry(idx);
        setTimeout(() => setSavedEntry(null), 1000);

        // Clear unsaved changes flag
        setHasUnsavedChanges(false);

        // Notify parent to update counters
        if (onInventoryUpdate) {
          onInventoryUpdate(currentType);
        }

        // Don't reload from API - local state is already updated
        // Reloading causes race condition where old data overwrites new data
      } catch (error: any) {
        console.error('Error auto-saving entry:', error);
        setSavingEntry(null);
        setSaveError(idx);
        
        // Check if it's an authentication error using the hook
        const isAuthError = error?.name === 'AuthenticationError' || 
                           error?.constructor?.name === 'AuthenticationError';
        
        if (isAuthError) {
          showAlert({
            title: t('step.4.inventory.dialog.session_expired_title'),
            message: t('step.4.inventory.dialog.session_expired_changes'),
            okText: t('step.4.inventory.dialog.ok')
          });
        }
      }
    }, 300);
  }, [currentType, data, getCurrentVersionName, onInventoryUpdate, t]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, []);

  // Periodic auto-save (every 10 seconds when entry is expanded)
  useEffect(() => {
    // Clear any existing interval
    if (periodicSaveIntervalRef.current) {
      clearInterval(periodicSaveIntervalRef.current);
    }

    // Only start periodic save if we have an expanded entry
    if (expandedIndex !== null && form && user) {
      periodicSaveIntervalRef.current = setInterval(() => {
        // autoSaveEntry already has dirty checking, so no need to duplicate
        autoSaveEntry(expandedIndex, form);
      }, 10000); // 10 seconds
    }

    return () => {
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, [expandedIndex, form, user, autoSaveEntry]);

  // Save on visibility change (when user switches browser tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && expandedIndex !== null && form) {
        // User switched to another browser tab - save their work
        autoSaveEntry(expandedIndex, form);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [expandedIndex, form, autoSaveEntry]);

  const getInventoryCounts = () => {
    const items = getCurrentData();
    const total = items.length;
    const complete = items.filter((item: any) => isComplete(currentType, item)).length;
    return { total, complete };
  };

  const handleTab = async (type: string) => {
    // Save current entry before switching tabs using immediate save
    if (expandedIndex !== null && form) {
      // Clear any pending debounced save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      const saved = await immediateSaveEntry(expandedIndex, form);
      if (!saved) {
        // Show error but still allow tab switch - user might want to abandon changes
        console.warn('Failed to save changes before tab switch');
      }
    }
    
    setCurrentType(type);
    setExpandedIndex(null);
    setEditingIndex(null);
    setForm(null);
  };

  // Handle modal close - save before closing
  const handleClose = async () => {
    // Clear any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // If there are unsaved changes, try to save immediately
    if (expandedIndex !== null && form) {
      const versionName = getCurrentVersionName();
      const currentData = data[currentType] || [];
      const hasChanges = JSON.stringify(form) !== JSON.stringify(currentData[expandedIndex]);
      
      if (hasChanges) {
        // Show saving indicator
        setSavingEntry(expandedIndex);
        
        const saved = await immediateSaveEntry(expandedIndex, form);
        
        if (!saved) {
          // Save failed - warn user
          setSavingEntry(null);
          showConfirmDialog({
            message: t('step.4.inventory.dialog.close_unsaved'),
            onConfirm: () => {
              // User chose to close anyway
              setHasUnsavedChanges(false);
              onClose();
            },
            confirmText: t('step.4.inventory.dialog.close_anyway'),
            cancelText: t('step.4.inventory.dialog.try_again'),
            variant: 'danger'
          });
          return;
        }
        
        setSavingEntry(null);
      }
    }
    
    // All saved, safe to close
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleEntryAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entryInput.trim()) return;

    // Check authentication before proceeding
    if (!user) {
      showAlert({
        title: t('step.4.inventory.dialog.not_logged_in_title'),
        message: t('step.4.inventory.dialog.not_logged_in_message'),
        okText: t('step.4.inventory.dialog.ok')
      });
      return;
    }

    const newItem = getDefaultItem(currentType);
    if (currentType === 'resentments') {
      newItem.who = entryInput.trim();
    } else if (currentType === 'fears') {
      newItem.fear = entryInput.trim();
    } else if (currentType === 'harms') {
      newItem.whoHarmed = entryInput.trim();
    }

    const currentData = getCurrentData();
    const newData = [...currentData, newItem];
    const versionName = getCurrentVersionName();
    
    // Store the input value before clearing it
    const inputValue = entryInput.trim();
    
    // Optimistically update UI
    setData(prev => ({
      ...prev,
      [currentType]: newData
    }));
    setEntryInput('');

    // Save to API with proper error handling
    try {
      // Use InventoryService which handles encryption properly
      await InventoryService.saveInventory(currentType, versionName, newData);
      
      if (onInventoryUpdate) {
        onInventoryUpdate(currentType);
      }
    } catch (error: any) {
      console.error('Error saving to API:', error);
      
      // Rollback the state change - remove the entry we just added
      setData(prev => ({
        ...prev,
        [currentType]: currentData
      }));
      
      // Restore the input value
      setEntryInput(inputValue);
      
      // Check if it's an authentication error
      const errorMessage = error?.message || String(error);
      const isAuthError = errorMessage.includes('401') || 
                         errorMessage.includes('Not logged in') ||
                         errorMessage.includes('Token expired') ||
                         errorMessage.includes('Invalid token') ||
                         errorMessage.includes('No authentication token');
      
      if (isAuthError) {
        showAlert({
          title: t('step.4.inventory.dialog.session_expired_title'),
          message: t('step.4.inventory.dialog.session_expired_entries'),
          okText: t('step.4.inventory.dialog.ok')
        });
      } else if (errorMessage.includes('Failed to encrypt')) {
        showAlert({
          title: t('step.4.inventory.dialog.encryption_error_title'),
          message: t('step.4.inventory.dialog.encryption_error_message'),
          okText: t('step.4.inventory.dialog.ok')
        });
      } else if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        showAlert({
          title: t('step.4.inventory.dialog.network_error_title'),
          message: t('step.4.inventory.dialog.network_error_message'),
          okText: t('step.4.inventory.dialog.ok')
        });
      } else {
        showAlert({
          title: t('step.4.inventory.dialog.save_failed_title'),
          message: t('step.4.inventory.dialog.save_failed_message'),
          okText: t('step.4.inventory.dialog.ok')
        });
      }
    }
  };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!form) return;
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, group: 'impacts' | 'defects') => {
    if (!form) return;
    const value = e.target.value;
    const currentArray = form[group] || [];
    
    if (e.target.checked) {
      setForm({ ...form, [group]: [...currentArray, value] });
    } else {
      setForm({ ...form, [group]: currentArray.filter((v: string) => v !== value) });
    }
  };

  // Handle field blur - no-op (form state is enough for visual feedback)
  const handleFieldBlur = () => {
    // Do nothing here - form state already updated by handleFormChange
    // This preserves dirty checking by NOT updating data state prematurely
    // Actual saves happen on: periodic (10s), collapse, close, visibility change
  };


  const handleDelete = (idx: number) => {
    const currentData = getCurrentData();
    const item = currentData[idx];
    
    // Get the first field value based on inventory type
    let firstFieldValue = '';
    
    const fallbackLabel = t('step.4.inventory.dialog.this_entry');
    if (currentType === 'resentments') {
      firstFieldValue = item.who || fallbackLabel;
    } else if (currentType === 'fears') {
      firstFieldValue = item.fear || fallbackLabel;
    } else if (currentType === 'harms') {
      firstFieldValue = item.whoHarmed || fallbackLabel;
    }
    
    const confirmMessage = t('step.4.inventory.dialog.delete_confirm', { name: firstFieldValue });
    
    showConfirmDialog({
      message: confirmMessage,
      onConfirm: async () => {
        // If deleting the currently expanded entry, save any other changes first
        if (expandedIndex === idx && form) {
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
        }
        
        const newData = currentData.filter((_: any, i: number) => i !== idx);
        const versionName = getCurrentVersionName();
        
        setData(prev => ({
          ...prev,
          [currentType]: newData
        }));

        // Save to API
        if (user) {
          try {
            await InventoryService.saveInventory(currentType, versionName, newData);
            
            // If all items are deleted, clear the completion status
            if (newData.length === 0) {
              await InventoryService.saveInventoryCompletion(currentType, versionName, false);
            }
            
            // Notify parent to update counters
            if (onInventoryUpdate) {
              onInventoryUpdate(currentType);
            }
            
            // Don't reload from API - local state is already updated
            // Reloading causes race condition
          } catch (error) {
            console.error('Error saving to API:', error);
          }
        }
      },
      variant: 'danger'
    });
  };

  const handleExpand = async (idx: number) => {
    // If we're expanding a different entry, save the current one first using immediate save
    if (expandedIndex !== null && expandedIndex !== idx && form) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      await immediateSaveEntry(expandedIndex, form);
    }
    
    // Toggle expansion
    if (expandedIndex === idx) {
      // Collapsing - save before collapsing using immediate save to ensure data is preserved
      if (form) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        const saved = await immediateSaveEntry(idx, form);
        if (!saved) {
          // Show error but still collapse - user can expand again if needed
          showAlert({
            message: t('step.4.inventory.dialog.collapse_save_warning'),
            variant: 'warning'
          });
        }
      }
      setExpandedIndex(null);
      setEditingIndex(null);
      setForm(null);
    } else {
      // Expanding - show edit form directly
      const currentData = getCurrentData();
      setExpandedIndex(idx);
      setEditingIndex(idx);
      setForm({ ...currentData[idx] });
    }
  };

  const handleStartOver = () => {
    const currentData = getCurrentData();
    const inventoryTypeLabel = currentTypeLabel();
    
    // If inventory is empty or doesn't exist, just start over without confirmation
    if (!currentData || currentData.length === 0) {
      // Clear the current inventory data
      setData(prev => {
        const newData = { ...prev };
        delete newData[currentType];
        return newData;
      });

      // Clear completion status even if inventory is empty
      if (user) {
        try {
          const versionName = getCurrentVersionName();
          InventoryService.saveInventoryCompletion(currentType, versionName, false);
          
          // Notify parent to update counters
          if (onInventoryUpdate) {
            onInventoryUpdate(currentType);
          }
          
          // Don't reload from API - local state is already updated
        } catch (error) {
          console.error('Error clearing completion status:', error);
        }
      }
      return;
    }
    
    // If inventory has content, show confirmation
    showConfirmDialog({
      message: t('step.4.inventory.dialog.start_over_confirm', { type: inventoryTypeLabel }),
      onConfirm: async () => {
        const versionName = getCurrentVersionName();
        
        // Clear the current inventory data
        setData(prev => {
          const newData = { ...prev };
          delete newData[currentType];
          return newData;
        });

        // Delete from API
        if (user) {
          try {
            await InventoryService.deleteInventory(currentType, versionName);
            // Also clear the completion status since all items are deleted
            await InventoryService.saveInventoryCompletion(currentType, versionName, false);
            
            // Notify parent to update counters
            if (onInventoryUpdate) {
              onInventoryUpdate(currentType);
            }
            
            // Don't reload from API - local state is already updated
          } catch (error) {
            console.error('Error deleting from API:', error);
          }
        }
      },
      variant: 'danger'
    });
  };





  const openHelp = (fieldId: string) => {
    const helpKey = `step.4.inventory.help.${fieldId.replace(/-/g, '_')}`;
    const resolved = t(helpKey);
    showHelpDialog({
      content: resolved === helpKey ? t('step.4.inventory.help_unavailable') : resolved,
      title: t('step.4.inventory.help_dialog_title')
    });
  };

  const handleDownload = async () => {
    try {
      const currentData = getCurrentData();
      
      if (currentData.length === 0) {
        showAlert({
          message: t('step.4.inventory.dialog.no_entries_download'),
          variant: 'info'
        });
        return;
      }

      // Encrypt the inventory data
      const encryptedData = await EncryptionService.encrypt({ items: currentData });
      
      // Create download data with metadata
      const downloadData = {
        metadata: {
          exportDate: new Date().toISOString(),
          inventoryType: currentType,
          version: getCurrentVersionName(),
          itemCount: currentData.length
        },
        encryptedData: encryptedData
      };

      const jsonData = JSON.stringify(downloadData, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `${currentType}-inventory-${date}.json`;

      // Try to use File System API for better UX (let user choose location)
      const supportsFileSystemAPI = 'showSaveFilePicker' in window;
      
      if (supportsFileSystemAPI) {
        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: t('step.4.inventory.file.encrypted_file'),
              accept: { 'application/json': ['.json'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(jsonData);
          await writable.close();
          
          showAlert({
            message: t('step.4.inventory.dialog.download_success', {
              count: currentData.length,
              type: currentTypeLabel()
            }),
            variant: 'success'
          });
          return;
        } catch (error) {
          // User cancelled or API failed, fall back to regular download
          if (error instanceof Error && error.name === 'AbortError') {
            return; // User cancelled, don't show error
          }
        }
      }

      // Fallback: Regular download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showAlert({
        message: t('step.4.inventory.dialog.download_success', {
          count: currentData.length,
          type: currentTypeLabel()
        }),
        variant: 'success'
      });
    } catch (error) {
      console.error('Download failed:', error);
      showAlert({
        message: t('step.4.inventory.dialog.download_failed'),
        variant: 'danger'
      });
    }
  };

  const handleCompletionCheckbox = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const currentData = getCurrentData();
    const total = currentData.length;
    const complete = currentData.filter((item: any) => isComplete(currentType, item)).length;
    
    
    
    // If no entries, don't allow checking
    if (total === 0) {
      return;
    }
    
    // If there are incomplete entries, show warning
    if (complete < total) {
      showAlert({
        message: t('step.4.inventory.dialog.incomplete_entries'),
        variant: 'warning'
      });
      return;
    }
    
    // If all entries are complete, allow the checkbox change
    setCompletionStatus(prev => {
      const newStatus = {
        ...prev,
        [currentType]: checked
      };
      
      return newStatus;
    });
    
    // Save completion status as part of the inventory data
    if (user) {
      try {

        
        // Update the completion status state
        setCompletionStatus(prev => ({
          ...prev,
          [currentType]: checked
        }));
        
        // Get current inventory data
        const currentData = getCurrentData();
        
        // Save the inventory data to API (without completion status)
        const versionName = getCurrentVersionName();
        await InventoryService.saveInventory(currentType, versionName, currentData);
        
        // Save completion status using the dedicated endpoint
        await InventoryService.saveInventoryCompletion(currentType, versionName, checked);
        
        // Update the local data state
        setData(prev => ({
          ...prev,
          [currentType]: currentData
        }));
        
        // Notify parent to update counters (completion affects status)
        if (onInventoryUpdate) {
          onInventoryUpdate(currentType);
        }
        
        // Don't reload from API - local state is already updated
        // Reloading causes race condition
      } catch (error) {
        console.error('Error saving completion status:', error);
        // Revert the state change if save fails
        setCompletionStatus(prev => ({
          ...prev,
          [currentType]: !checked
        }));
      }
    }
  };

  const handleUpload = async () => {
    try {
      let file: File;
      
      // Try to use File System API for better UX
      const supportsFileSystemAPI = 'showOpenFilePicker' in window;
      
      if (supportsFileSystemAPI) {
        try {
          const [fileHandle] = await (window as any).showOpenFilePicker({
            types: [{
              description: t('step.4.inventory.file.encrypted_files'),
              accept: { 'application/json': ['.json'] }
            }],
            multiple: false
          });
          file = await fileHandle.getFile();
        } catch (error) {
          // User cancelled
          if (error instanceof Error && error.name === 'AbortError') {
            return;
          }
          throw error;
        }
      } else {
        // Fallback: Traditional file input
        file = await new Promise<File>((resolve, reject) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.json';
          input.onchange = (e) => {
            const selectedFile = (e.target as HTMLInputElement).files?.[0];
            if (selectedFile) {
              resolve(selectedFile);
            } else {
              reject(new Error(t('step.4.inventory.upload.no_file')));
            }
          };
          input.click();
        });
      }

      // Check file extension
      if (!file.name.toLowerCase().endsWith('.json')) {
        setImportStatus({ text: t('step.4.inventory.upload.not_json'), error: true });
        setTimeout(() => setImportStatus(null), 5000);
        return;
      }

      setImportStatus({ text: t('step.4.inventory.upload.reading'), error: false });

      const text = await file.text();
      const uploadedData = JSON.parse(text);
      
      // Validate structure
      if (!uploadedData.metadata || !uploadedData.encryptedData) {
        throw new Error(t('step.4.inventory.upload.invalid_format'));
      }

      // Check if the inventory type matches
      if (uploadedData.metadata.inventoryType !== currentType) {
        throw new Error(
          t('step.4.inventory.upload.type_mismatch', {
            fileType: typeLabel(uploadedData.metadata.inventoryType),
            currentType: currentTypeLabel()
          })
        );
      }

      // Decrypt the data
      const decryptedData = await EncryptionService.decrypt(uploadedData.encryptedData);
      const parsedData = decryptedData.items || [];

      if (parsedData.length === 0) {
        throw new Error(t('step.4.inventory.upload.no_data'));
      }

      // Replace current inventory data with uploaded data
      const versionName = getCurrentVersionName();
      
      setData(prev => ({
        ...prev,
        [currentType]: parsedData
      }));

      // Save to API
      if (user) {
        try {
          await InventoryService.saveInventory(currentType, versionName, parsedData);
          
          // Don't reload from API - local state is already updated
          // Reloading causes race condition
        } catch (error) {
          console.error('Error saving to API:', error);
          throw new Error(t('step.4.inventory.upload.save_failed'));
        }
      }
      
      setImportStatus({
        text: t('step.4.inventory.upload.success', { count: parsedData.length }),
        error: false
      });
      setTimeout(() => setImportStatus(null), 3000);
      
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : t('step.4.inventory.upload.unknown_error');
      setImportStatus({
        text: t('step.4.inventory.upload.failed', { error: errorMessage }),
        error: true
      });
      setTimeout(() => setImportStatus(null), 5000);
    }
  };



  if (!isOpen) return null;

  // Entry label and placeholder
  const entryLabel = t(`step.4.inventory.entry.${currentType}.label`);
  const entryPlaceholder = t(`step.4.inventory.entry.${currentType}.placeholder`);
  const welcomeText = t(`step.4.inventory.entry.${currentType}.welcome`);
  const myLabel = t(
    INVENTORY_TYPES.find(type => type.key === currentType)?.myLabelKey ||
      `step.4.inventory.my.${currentType}`
  );

  const showWelcome = getCurrentData().length === 0;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{t('step.4.inventory.modal_title')}</h2>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={t('step.4.inventory.close')}
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Desktop tabs */}
          <div className={`${styles.inventoryTabs} ${styles.inventoryTabsDesktop}`}>
            {INVENTORY_TYPES.map((type) => {
              const status = inventoryStatuses?.find(s => s.type === type.key);
              const total = status?.total || 0;
              const isCompleted = status?.isCompleted || false;

              let statusText = '';
              let bulletColor = '';

              if (total === 0) {
                statusText = t('step.4.inventory.status.not_started');
                bulletColor = '#9ca3af';
              } else if (isCompleted) {
                statusText = t('step.4.inventory.status.completed', { count: total });
                bulletColor = '#10b981';
              } else {
                statusText = t('step.4.inventory.status.in_progress', { count: total });
                bulletColor = '#fbbf24';
              }

              return (
                <button
                  key={type.key}
                  type="button"
                  onClick={() => handleTab(type.key)}
                  className={`${styles.tabBtn} ${currentType === type.key ? styles.active : ''}`}
                >
                  <span className={styles.tabLabel}>{t(type.labelKey)}</span>
                  <span className={styles.tabBullet} style={{ color: bulletColor }}>•</span>
                  <span className={styles.tabStatus}>{statusText}</span>
                </button>
              );
            })}
          </div>

          {/* Mobile: progress + horizontal tabs with per-inventory state */}
          <div className={styles.inventoryMobileChrome}>
            <p className={styles.mobileProgressText}>
              {t('step.4.inventory.mobile.progress', {
                done: completedInventoryCount,
                total: INVENTORY_TYPES.length,
              })}
            </p>
            <div className={styles.inventoryTabsMobileCenter}>
              <div className={styles.inventoryTabsMobile} role="tablist" aria-label={t('step.4.inventory.modal_title')}>
              {INVENTORY_TYPES.map((type) => {
                const visual = getInventoryTabVisualState(type.key);
                const isActive = currentType === type.key;
                return (
                  <button
                    key={type.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleTab(type.key)}
                    className={`${styles.mobileTabBtn} ${isActive ? styles.mobileTabBtnActive : ''}`}
                  >
                    <span className={styles.mobileTabLabel}>{t(type.labelKey)}</span>
                    <span
                      className={`${styles.mobileTabStateIcon} ${styles[`mobileTabState_${visual}`]}`}
                      aria-hidden
                    >
                      {mobileTabStateIcon(visual)}
                    </span>
                  </button>
                );
              })}
              </div>
            </div>
          </div>

          {/* Desktop header + completion */}
          <div className={`${styles.inventoryHeader} ${styles.inventoryHeaderDesktop}`}>
            <h3 className={styles.inventoryHeaderTitle}>
              {t('step.4.inventory.header_title', { type: currentTypeLabel() })}
              <button
                type="button"
                className={styles.helpBtn}
                onClick={() => openHelp('inventory')}
                title={t('step.4.inventory.help_inventories_title')}
                tabIndex={-1}
              >
                ?
              </button>
            </h3>

            <div className={styles.completionCheckboxCentered}>
              <label className={styles.completionLabel}>
                <input
                  type="checkbox"
                  checked={completionStatus[currentType] || false}
                  onChange={handleCompletionCheckbox}
                  disabled={getCurrentData().length === 0}
                  className={styles.completionInput}
                />
                <span className={styles.completionText}>{t('step.4.inventory.completed_checkbox')}</span>
              </label>
            </div>
          </div>

          {/* Mobile header + per-inventory completion */}
          <div className={styles.inventoryHeaderMobile}>
            <h3 className={styles.mobileActiveTitle}>{currentTypeLabel()}</h3>
            <div className={styles.mobileCompletionBlock}>
              <label className={styles.completionLabel}>
                <input
                  type="checkbox"
                  checked={completionStatus[currentType] || false}
                  onChange={handleCompletionCheckbox}
                  disabled={getCurrentData().length === 0}
                  className={styles.completionInput}
                />
                <span className={styles.completionText}>{t('step.4.inventory.completed_checkbox')}</span>
              </label>
            </div>
          </div>
          
          {importStatus && (
            <div className={`${styles.importStatus} ${importStatus.error ? styles.error : styles.success}`}>
              {importStatus.text}
            </div>
          )}





          {/* Entry Add and Inventory List - always show */}
          {/* Entry Add */}
              <form className={styles.quickForm} onSubmit={handleEntryAdd}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {entryLabel} 
                    <button 
                      type="button" 
                      className={styles.helpBtn} 
                      onClick={() => openHelp(`${currentType === 'resentments' ? 'resentment' : currentType === 'fears' ? 'fear' : 'harm'}-${currentType === 'resentments' ? 'who' : currentType === 'fears' ? 'what' : 'who'}`)}
                      title={t('step.4.inventory.help_field_title')}
                      tabIndex={-1}
                    >
                      ?
                    </button>
                  </label>
                  <div className={styles.quickInputGroup}>
                    <input
                      type="text"
                      value={entryInput}
                      onChange={e => setEntryInput(e.target.value)}
                      placeholder={entryPlaceholder}
                      required
                      className={styles.quickInput}
                    />
                    <button type="submit" className={styles.btnSuccess}>{t('step.4.inventory.add_to_list')}</button>
                  </div>
                </div>
              </form>

              {/* Welcome Screen */}
              {showWelcome && (
                <div className={styles.welcomeScreen}>
                  <p>{welcomeText}</p>
                </div>
              )}

              {/* Inventory List */}
              {(!showWelcome && getCurrentData().length > 0) && (
                <div className={styles.inventoryList}>
                  <h4 className={styles.inventoryTitle}>
                    {myLabel}
                    <span className={styles.inventoryCount}>
                      {t('step.4.inventory.counts', {
                        total: getInventoryCounts().total,
                        complete: getInventoryCounts().complete
                      })}
                    </span>
                  </h4>
                  <ul className={styles.inventoryItems}>
                    {getCurrentData().map((item: any, idx: number) => {
                      const isExpanded = expandedIndex === idx;
                      let mainLabel = '';
                      if (currentType === 'resentments') mainLabel = item.who;
                      if (currentType === 'fears') mainLabel = item.fear;
                      if (currentType === 'harms') mainLabel = item.whoHarmed;
                      const complete = isComplete(currentType, item);
                      
                      return (
                        <li
                          key={idx}
                          className={`${styles.inventoryItem} ${complete ? styles.completeEntry : styles.incompleteEntry}`}
                        >
                          <div className={styles.itemSummary} onClick={() => handleExpand(idx)}>
                            <div className={styles.itemText}>
                              <span className={styles.expandIcon}>{isExpanded ? '▲' : '▼'}</span>
                              <span className={styles.itemLabel}>{mainLabel}</span>
                              {savingEntry === idx && (
                                <span className={styles.savingText}>{t('step.4.inventory.saving')}</span>
                              )}
                              {savedEntry === idx && (
                                <span className={styles.savedText}>✓ {t('step.4.inventory.saved')}</span>
                              )}
                              {saveError === idx && (
                                <span className={styles.saveErrorText}>⚠ {t('step.4.inventory.save_failed_badge')}</span>
                              )}
                              {savingEntry !== idx && savedEntry !== idx && saveError !== idx && (
                                <>
                                  {complete && (
                                    <span className={styles.completeIcon}>✓</span>
                                  )}
                                  {!complete && (
                                    <span className={styles.incompleteText}>{t('step.4.inventory.click_for_details')}</span>
                                  )}
                                </>
                              )}
                            </div>
                            <div className={styles.itemControls}>
                              <button 
                                onClick={e => { e.stopPropagation(); handleDelete(idx); }} 
                                className={styles.deleteButton}
                                title={t('step.4.inventory.delete')}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          
                          {/* Edit Form (inline, shown directly when expanded) */}
                          {isExpanded && (
                            <div className={styles.inventoryForm}>
                                
                                {currentType === 'resentments' && (
                                  <>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.who.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-who')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <input 
                                        name="who" 
                                        placeholder={t('step.4.inventory.field.who.placeholder')} 
                                        value={form?.who || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        required 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.why.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-why')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="why" 
                                        placeholder={t('step.4.inventory.field.why.placeholder')} 
                                        value={form?.why || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.feeling.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-feeling')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="feeling" 
                                        placeholder={t('step.4.inventory.field.feeling.placeholder')} 
                                        value={form?.feeling || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={2} 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.impact.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-impact')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <div className={styles.checkboxGroup} onBlur={handleFieldBlur}>
                                        {IMPACT_OPTIONS.map(opt => (
                                          <label key={opt.value} className={styles.checkboxItem}>
                                            <input
                                              type="checkbox"
                                              name="impacts"
                                              value={opt.value}
                                              checked={form?.impacts?.includes(opt.value) || false}
                                              onChange={e => handleCheckboxChange(e, 'impacts')}
                                            />
                                            {t(opt.labelKey)}
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.my_part.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-part')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="myPart" 
                                        placeholder={t('step.4.inventory.field.my_part.placeholder')} 
                                        value={form?.myPart || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>
                                  </>
                                )}
                                
                                {currentType === 'fears' && (
                                  <>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.fear.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('fear-what')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <input 
                                        name="fear" 
                                        placeholder={t('step.4.inventory.field.fear.placeholder')} 
                                        value={form?.fear || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        required 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.fear_why.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('fear-why')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="why" 
                                        placeholder={t('step.4.inventory.field.fear_why.placeholder')} 
                                        value={form?.why || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.fear_impact.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('fear-impact')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <div className={styles.checkboxGroup} onBlur={handleFieldBlur}>
                                        {IMPACT_OPTIONS.map(opt => (
                                          <label key={opt.value} className={styles.checkboxItem}>
                                            <input
                                              type="checkbox"
                                              name="impacts"
                                              value={opt.value}
                                              checked={form?.impacts?.includes(opt.value) || false}
                                              onChange={e => handleCheckboxChange(e, 'impacts')}
                                            />
                                            {t(opt.labelKey)}
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  </>
                                )}
                                
                                {currentType === 'harms' && (
                                  <>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.who_harmed.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('harm-who')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <input 
                                        name="whoHarmed" 
                                        placeholder={t('step.4.inventory.field.who_harmed.placeholder')} 
                                        value={form?.whoHarmed || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        required 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.how.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('harm-how')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="how" 
                                        placeholder={t('step.4.inventory.field.how.placeholder')} 
                                        value={form?.how || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>

                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.defects.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('harm-character')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <div className={styles.checkboxGroup} onBlur={handleFieldBlur}>
                                        {DEFECT_OPTIONS.map(opt => (
                                          <label key={opt.value} className={styles.checkboxItem}>
                                            <input
                                              type="checkbox"
                                              name="defects"
                                              value={opt.value}
                                              checked={form?.defects?.includes(opt.value) || false}
                                              onChange={e => handleCheckboxChange(e, 'defects')}
                                            />
                                            {t(opt.labelKey)}
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        {t('step.4.inventory.field.alternative.label')}{' '}
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('harm-alternative')}
                                          title={t('step.4.inventory.help_field_title')}
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="alternative" 
                                        placeholder={t('step.4.inventory.field.alternative.placeholder')} 
                                        value={form?.alternative || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>
                                  </>
                                )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
        </div>
      </div>
    </div>
  );
};

export default InventoryModal; 